<template>
  <div class="flex flex-col px-0 md:px-4">
    <SwitchOptions
      :is-collection="configuratorStore.isCollectionProduct"
      class="md:order-1"
    />

    <ProductFilters
      v-if="!configuratorStore.isUploadConfigurator"
      :show-prices="configuratorStore.isCollectionProduct"
      :show-radio-buttons="configuratorStore.isUploadConfigurator"
      class="md:order-2"
      @changed-filter="onFilterChange"
    />

    <DefaultTextInput
      v-if="
        configuratorStore.isLetterCollageConfigurator &&
        !configuratorStore.mediaclipUserText
      "
      v-model="configuratorStore.mediaclipUserInputText"
      :label="I18n.global.t('pages.product_detail_page.user_text.label')"
      :required="true"
      class="mt-3 md:order-3"
      name="product_id"
      @update:model-value="handleMediaclipUserInputText"
    />

    <InStock :loading="changingFilter" class="mt-0 md:order-4 md:mt-2" />

    <PriceDisplay
      v-bind="{
        basePrice,
        finalPrice,
      }"
      class="md:order-5"
    />

    <div v-auto-animate>
      <PaazlTruckDelivery
        v-if="paazlStore.hasTomorrowDelivery"
        :paazl-date="$t('general.delivery_options.tomorrow_delivery.label')"
        :paazl-description="paazlStore.urgentDeliveryDescription"
        class="md:order-6"
      />
    </div>

    <div class="mt-5 md:order-7">
      <ProductInfoButtons />

      <em
        v-if="configuratorStore.isUploadConfigurator"
        v-t="'pages.product_detail_page.after_upload_text'"
        class="mt-2 block text-center italic text-gray"
      />
    </div>

    <div v-if="hasTimer" class="mt-4 md:order-8 md:hidden">
      <ProductTimer />
    </div>

    <UspItems class="mt-5 md:order-9 md:mt-6" />
  </div>

  <div class="mx-0 mt-5 md:mx-4">
    <DeliveryOptions />
  </div>

  <!-- Price table -->
  <PriceTable
    v-if="hasPriceTableData && !configuratorStore.isSimpleConfigurator"
    class="md:hidden"
  />

  <Transition
    enter-active-class="transition ease-in duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <ExtraOptionsModal v-if="configuratorStore.extraOptionsModalOpen" />
  </Transition>
</template>

<script setup lang="ts">
import InStock from '@/pages/ProductPage/partials/ProductDetailPage/InStock.vue';
import ProductTimer from '@/components/ProductTimer.vue';
import UspItems from '@/pages/ProductPage/partials/ProductDetailPage/UspItems.vue';
import PaazlTruckDelivery from '@/pages/ProductPage/partials/ProductDetailPage/PaazlTruckDelivery.vue';
import DeliveryOptions from '@/pages/ProductPage/partials/ProductDetailPage/DeliveryOptions.vue';
import PriceDisplay from '@/pages/ProductPage/partials/ProductDetailPage/PriceDisplay.vue';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore.ts';
import {
  computed,
  type ComputedRef,
  onBeforeMount,
  type Ref,
  ref,
  watch,
} from 'vue';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';
import { usePaazlStore } from '@/stores/Paazl/PaazlStore.ts';
import ProductInfoButtons from '@/pages/ProductPage/partials/ProductInfoButtons.vue';
import { useExtraOptions } from '@/composables/UseExtraOptions.ts';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore.ts';
import ExtraOptionsModal from '@/pages/ProductPage/partials/Collection/ExtraOptionsModal.vue';
import SwitchOptions from '@/pages/ProductPage/partials/Collection/SwitchOptions.vue';
import I18n from '@/i18n.ts';
import DefaultTextInput from '@/components/Forms/DefaultTextInput.vue';
import ProductFilters from '@/pages/ProductPage/partials/ProductFilters/ProductFilters.vue';
import { usePaazl } from '@/composables/Paazl.ts';
import PriceTable from '@/pages/ProductPage/partials/PriceTable/PriceTable.vue';
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import {
  amountHexagonsKey,
  useFilterStore,
} from '@/stores/Configurator/FilterStore.ts';

const paazlStore = usePaazlStore();
const extraOptions = useExtraOptions();
const extraOptionsStore = useExtraOptionsStore();
const paazlComposable = usePaazl();
const configuratorStore = useConfiguratorStore();
const sizeStore = useSizeStore();
const filterStore = useFilterStore();

const changingFilter = ref(false);

const hasTimer: ComputedRef<boolean> = computed(() => !!window.timer);
const hasPriceTableData: ComputedRef<boolean> = computed(
  () => !!window.price_table
);

const showLowestPrice: ComputedRef<boolean> = computed(() => {
  if (configuratorStore.isCollectionProduct) {
    return (
      sizeStore.selectedSizeValue.attributes.price ===
      window.product_prices.final_price
    );
  }

  if (
    !!configuratorStore.mediaclipProjectId &&
    configuratorStore.isConfiguratorOpen
  ) {
    setPrices();
    return false;
  }
  return false;
});
const basePriceRef: Ref<number | undefined> = ref(
  window.product_prices.base_price
);

const basePrice: ComputedRef<number | undefined> = computed(() =>
  getPrice('recommendedPrice', basePriceRef)
);

const finalPriceRef: Ref<number | undefined> = ref(
  window.product_prices.final_price
);

const finalPrice: ComputedRef<number | undefined> = computed(() =>
  getPrice('price', finalPriceRef)
);

function getPrice(priceKey: string, priceRef: Ref<number | undefined>) {
  // Show configurator price
  return (
    (showLowestPrice.value
      ? useSizeStore().selectedSizeValue.attributes[priceKey]
      : priceRef.value) / 100
  );
}

const handleMediaclipUserInputText = () => {
  if (configuratorStore.mediaclipInitialized) {
    configuratorStore.resetMediaclipProject();
  }
};

async function requestExtraOptions(): Promise<void> {
  await extraOptions.requestCategories(
    sizeStore.selectedSizeValue.attributes.sku!,
    configuratorStore.isCollectionProduct
  );
  extraOptionsStore.selectDefaultProductOptions();
}

async function onFilterChange(
  filter: string | undefined,
  value: SelectorOption | undefined
): Promise<void> {
  if (
    value?.value === filterStore.selectedFilterOptions[filter!]?.value ||
    value?.value === sizeStore.previousSelectedSizeOption.value
  ) {
    return;
  }

  // Set the current selected as previously selected
  sizeStore.previousSelectedSizeOption = sizeStore.selectedSizeOption;

  // If a filter is provided, and it's an amount field (e.g., hexagons), update the base and final prices accordingly
  if (filter && value && filter === amountHexagonsKey) {
    basePriceRef.value =
      (window.product_prices.base_price ?? 0) * Number(value.value);
    finalPriceRef.value =
      (window.product_prices.final_price ?? 0) * Number(value.value);
  } else {
    setPrices();
  }

  changingFilter.value = true;
  await paazlStore.requestShippingOptions(paazlComposable.getRequestData());

  changingFilter.value = false;
}

function setPrices(): void {
  basePriceRef.value =
    sizeStore.selectedSizeValue.attributes.recommendedPrice ?? undefined;
  finalPriceRef.value =
    sizeStore.selectedSizeValue.attributes.price ?? undefined;
}

function setLowestPricesOnPageLoad(): void {
  basePriceRef.value = configuratorStore.mediaclipProjectId
    ? sizeStore.selectedSizeValue.attributes.recommendedPrice
    : window.product_prices.base_price;
  finalPriceRef.value = configuratorStore.mediaclipProjectId
    ? sizeStore.selectedSizeValue.attributes.price
    : window.product_prices.final_price;

  if (configuratorStore.isCollectionProduct) {
    sizeStore.selectedSizeOption = sizeStore.getCheapestProduct(
      sizeStore.allAvailableSizeValues
    );
  }
}

onBeforeMount(() => {
  setLowestPricesOnPageLoad();
  paazlStore.requestShippingOptions(paazlComposable.getRequestData(true));
  requestExtraOptions().then(() => {})
});

watch(
  () => sizeStore.selectedSizeValue,
  newValue => {
    if (newValue && newValue.attributes) {
      setPrices();
    }
  }
);
</script>
